import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase/config.js';
import {
  collection, query, where, getDocs, doc, updateDoc, deleteDoc, orderBy, writeBatch
} from 'firebase/firestore';
import { format, parseISO, isValid as isValidDate } from 'date-fns';

const SubPayManagement = ({ currentUser }) => {
  const [substitutes, setSubstitutes] = useState([]);
  const [selectedSub, setSelectedSub] = useState(null);
  const [subJobs, setSubJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [editingJobId, setEditingJobId] = useState(null);
  const [editingPayAmount, setEditingPayAmount] = useState('');
  const [dailyPayMethods, setDailyPayMethods] = useState({});
  const [filterSubType, setFilterSubType] = useState('all');
  const [showAddInternalSubs, setShowAddInternalSubs] = useState(false);
  const [allFacultyStaff, setAllFacultyStaff] = useState([]);
  const [loadingFaculty, setLoadingFaculty] = useState(false);

  // Check if user has HR privileges
  const isHR = currentUser?.role === 'HR' || currentUser?.hasElevatedPrivileges;

  // Fetch all faculty/staff for the "Add Internal Subs" section
  const fetchAllFacultyStaff = useCallback(async () => {
    if (!currentUser?.tenantId) return;

    try {
      setLoadingFaculty(true);
      const facultyQuery = query(
        collection(db, 'faculty_staff'),
        where('tenantId', '==', currentUser.tenantId)
      );
      
      const facultySnapshot = await getDocs(facultyQuery);
      const facultyList = facultySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setAllFacultyStaff(facultyList);
    } catch (err) {
      console.error('Error fetching faculty/staff:', err);
    } finally {
      setLoadingFaculty(false);
    }
  }, [currentUser]);

  // Enable pay tracking for an internal employee
  const handleEnablePayTracking = async (facultyMember) => {
  if (!window.confirm(`Enable pay tracking for ${facultyMember.name}? This will allow them to appear in the Sub Pay Management system.`)) {
    return;
  }

  try {
    setSaving(true);
    
    // Set default pay rates AND enable pay tracking flag
    const updates = {
      dailyRate: facultyMember.dailyRate || 0,
      periodRate: facultyMember.periodRate || facultyMember.payRate || 0,
      dailyMaxLimit: facultyMember.dailyMaxLimit || facultyMember.dailyRate || 0,
      payTrackingEnabled: true  // ADD THIS LINE
    };
    
    await updateDoc(doc(db, 'faculty_staff', facultyMember.id), updates);
    
    alert(`Pay tracking enabled for ${facultyMember.name}. You can now configure their rates.`);
    
    // Refresh the lists
    await fetchSubstitutes();
    await fetchAllFacultyStaff();
    
  } catch (err) {
    console.error('Error enabling pay tracking:', err);
    alert('Failed to enable pay tracking.');
  } finally {
    setSaving(false);
  }
};

  // Get or set pay method for a specific date
  const getPayMethodForDate = (dateKey) => {
    return dailyPayMethods[dateKey] || 'daily';
  };

  const setPayMethodForDate = (dateKey, method) => {
    setDailyPayMethods(prev => ({
      ...prev,
      [dateKey]: method
    }));
  };

  // Calculate daily pay based on method selected for that specific date
  const calculateDailyPay = (jobsForDay, dateKey) => {
    if (!selectedSub || !jobsForDay.length) return 0;
    
    const method = getPayMethodForDate(dateKey);
    
    const dailyRate = parseFloat(
      selectedSub.dailyRate || 
      selectedSub.payRate || 
      selectedSub.rate || 
      0
    );
    
    const periodRate = parseFloat(
      selectedSub.periodRate || 
      selectedSub.payRate || 
      selectedSub.rate || 
      0
    );
    
    const periodsWorked = jobsForDay.length;
    
    if (method === 'daily') {
      return dailyRate;
    } else {
      return periodsWorked * periodRate;
    }
  };

  // Handle marking all jobs for a day as paid
  const handleMarkDayAsPaid = async (jobs, dateKey) => {
    if (!jobs.length) return;
    
    const confirmMessage = `Mark all ${jobs.length} job(s) for this day as PAID?`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setSaving(true);
      
      const batch = writeBatch(db);
      
      const method = getPayMethodForDate(dateKey);
      const dailyRate = parseFloat(selectedSub.dailyRate || selectedSub.payRate || selectedSub.rate || 0);
      const periodRate = parseFloat(selectedSub.periodRate || selectedSub.payRate || selectedSub.rate || 0);
      
      let individualPayAmount;
      if (method === 'daily') {
        individualPayAmount = jobs.length > 0 ? dailyRate / jobs.length : 0;
      } else {
        individualPayAmount = periodRate;
      }
      
      jobs.forEach(job => {
        const jobRef = doc(db, 'sub_jobs', job.id);
        batch.update(jobRef, {
          status: 'Paid',
          payAmount: individualPayAmount
        });
      });
      
      await batch.commit();
      
      if (selectedSub) {
        await fetchSubJobs(selectedSub.id);
      }
      
      alert(`Successfully marked ${jobs.length} job(s) as paid for this day.`);
    } catch (err) {
      console.error('Error marking day as paid:', err);
      alert('Failed to mark day as paid.');
    } finally {
      setSaving(false);
    }
  };

  // Handle marking all jobs for a day as unpaid
  const handleMarkDayAsUnpaid = async (jobs) => {
    if (!jobs.length) return;
    
    const confirmMessage = `Mark all ${jobs.length} job(s) for this day as UNPAID?`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setSaving(true);
      
      const batch = writeBatch(db);
      
      jobs.forEach(job => {
        const jobRef = doc(db, 'sub_jobs', job.id);
        batch.update(jobRef, {
          status: 'Unpaid'
        });
      });
      
      await batch.commit();
      
      if (selectedSub) {
        await fetchSubJobs(selectedSub.id);
      }
      
      alert(`Successfully marked ${jobs.length} job(s) as unpaid for this day.`);
    } catch (err) {
      console.error('Error marking day as unpaid:', err);
      alert('Failed to mark day as unpaid.');
    } finally {
      setSaving(false);
    }
  };

  // PDF Report Generation Functions
  const generateIndividualPDF = useCallback(() => {
    if (!selectedSub) return;

    const groupedJobs = groupJobsByDate(subJobs);
    const totalPaidForYear = groupedJobs.reduce((sum, day) => {
      const paidJobs = day.jobs.filter(job => job.status === 'Paid');
      if (paidJobs.length === 0) return sum;
      
      const method = getPayMethodForDate(day.dateKey);
      const dailyRate = parseFloat(
        selectedSub.dailyRate || 
        selectedSub.payRate || 
        selectedSub.rate || 
        0
      );
      const periodRate = parseFloat(
        selectedSub.periodRate || 
        selectedSub.payRate || 
        selectedSub.rate || 
        0
      );
      
      if (method === 'daily') {
        return sum + dailyRate;
      } else {
        return sum + (paidJobs.length * periodRate);
      }
    }, 0);

    const paidJobs = groupedJobs.flatMap(day => 
      day.jobs.filter(job => job.status === 'Paid')
    );

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Pay Report - ${selectedSub.name}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 40px; color: #333; }
          .header { text-align: center; margin-bottom: 30px; }
          .company-name { font-size: 24px; font-weight: bold; color: #2c3e50; }
          .report-title { font-size: 18px; margin-top: 10px; }
          .contractor-info { background: #f8f9fa; padding: 20px; border-radius: 8px; margin-bottom: 30px; }
          .sub-type-badge { display: inline-block; padding: 4px 12px; border-radius: 12px; font-size: 12px; font-weight: bold; margin-left: 10px; }
          .external-badge { background: #e3f2fd; color: #1565c0; }
          .internal-badge { background: #f3e5f5; color: #6a1b9a; }
          .summary-box { background: #e8f4f8; padding: 20px; border-radius: 8px; margin-bottom: 30px; text-align: center; }
          .total-amount { font-size: 28px; font-weight: bold; color: #2c3e50; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 30px; }
          th, td { padding: 12px; text-align: left; border-bottom: 1px solid #ddd; }
          th { background-color: #f2f2f2; font-weight: bold; }
          .date-group { background-color: #f8f9fa; font-weight: bold; }
          .amount { text-align: right; }
          .footer { margin-top: 40px; font-size: 12px; color: #666; text-align: center; }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="company-name">${currentUser?.tenantName || 'Relief Roster'}</div>
          <div class="report-title">Pay Report - Year ${selectedYear}</div>
        </div>
        
        <div class="contractor-info">
          <h3>Substitute Information 
            <span class="sub-type-badge ${selectedSub.subType === 'internal' ? 'internal-badge' : 'external-badge'}">
              ${selectedSub.subType === 'internal' ? 'Internal Employee' : 'External Contractor'}
            </span>
          </h3>
          <p><strong>Name:</strong> ${selectedSub.name}</p>
          <p><strong>Email:</strong> ${selectedSub.email || 'N/A'}</p>
          <p><strong>Role:</strong> ${selectedSub.role}</p>
          <p><strong>Daily Rate:</strong> $${selectedSub.dailyRate || 0}</p>
          <p><strong>Period Rate:</strong> $${selectedSub.periodRate || selectedSub.payRate || 0}</p>
        </div>
        
        <div class="summary-box">
          <h2>Total Compensation for ${selectedYear}</h2>
          <div class="total-amount">$${totalPaidForYear.toFixed(2)}</div>
          <p>Based on ${paidJobs.length} paid job(s)</p>
        </div>
        
        <h3>Detailed Job History</h3>
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Description</th>
              <th>Pay Method</th>
              <th class="amount">Amount</th>
            </tr>
          </thead>
          <tbody>
            ${groupedJobs.map(day => {
              const dayPaidJobs = day.jobs.filter(job => job.status === 'Paid');
              if (dayPaidJobs.length === 0) return '';
              
              const payMethod = getPayMethodForDate(day.dateKey);
              const dayTotal = payMethod === 'daily' 
                ? parseFloat(selectedSub.dailyRate || 0)
                : dayPaidJobs.length * parseFloat(selectedSub.periodRate || selectedSub.payRate || 0);
              
              return `
                <tr class="date-group">
                  <td colspan="3">${day.formattedDate} - ${payMethod === 'daily' ? 'Daily Rate' : 'Per Period'}</td>
                  <td class="amount">$${dayTotal.toFixed(2)}</td>
                </tr>
                ${dayPaidJobs.map(job => `
                  <tr>
                    <td></td>
                    <td>${job.description || `${job.department || 'N/A'} - Period ${job.period || 'N/A'}`}</td>
                    <td>${payMethod === 'daily' ? 'Daily Rate' : 'Per Period'}</td>
                    <td class="amount">$${parseFloat(job.payAmount || 0).toFixed(2)}</td>
                  </tr>
                `).join('')}
              `;
            }).join('')}
          </tbody>
        </table>
        
        <div class="footer">
          <p>Report generated on ${format(new Date(), 'MMMM d, yyyy')} by Relief Roster</p>
          <p>This report shows only jobs marked as "Paid" for tax reporting purposes.</p>
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.print();
  }, [selectedSub, subJobs, selectedYear, dailyPayMethods, currentUser, getPayMethodForDate]);

  const generateAllSubsPDF = useCallback(async () => {
    try {
      setLoading(true);
      
      const allSubsData = [];
      let grandTotal = 0;
      
      for (const sub of substitutes) {
        const jobsQuery = query(
          collection(db, 'sub_jobs'),
          where('substituteId', '==', sub.id),
          where('tenantId', '==', currentUser.tenantId)
        );
        
        const jobsSnapshot = await getDocs(jobsQuery);
        const jobs = jobsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          jobDate: doc.data().jobDate?.toDate ? doc.data().jobDate.toDate() : 
                   (typeof doc.data().jobDate === 'string' ? parseISO(doc.data().jobDate) : null)
        }));
        
        const yearPaidJobs = jobs.filter(job => 
          job.jobDate?.getFullYear() === selectedYear && job.status === 'Paid'
        );
        
        if (yearPaidJobs.length > 0) {
          const grouped = {};
          yearPaidJobs.forEach(job => {
            const dateKey = job.jobDate ? format(job.jobDate, 'yyyy-MM-dd') : 'invalid';
            if (!grouped[dateKey]) grouped[dateKey] = [];
            grouped[dateKey].push(job);
          });
          
          let subTotal = 0;
          Object.entries(grouped).forEach(([dateKey, dayJobs]) => {
            const payMethod = getPayMethodForDate(dateKey) || 'daily';
            if (payMethod === 'daily') {
              subTotal += parseFloat(sub.dailyRate || 0);
            } else {
              subTotal += dayJobs.length * parseFloat(sub.periodRate || sub.payRate || 0);
            }
          });
          
          allSubsData.push({
            sub,
            jobs: yearPaidJobs,
            grouped,
            total: subTotal
          });
          
          grandTotal += subTotal;
        }
      }
      
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Annual Sub Pay Report - ${selectedYear}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 40px; color: #333; }
            .header { text-align: center; margin-bottom: 30px; }
            .company-name { font-size: 24px; font-weight: bold; color: #2c3e50; }
            .report-title { font-size: 18px; margin-top: 10px; }
            .summary-box { background: #e8f4f8; padding: 20px; border-radius: 8px; margin-bottom: 30px; text-align: center; }
            .grand-total { font-size: 32px; font-weight: bold; color: #2c3e50; }
            .sub-section { margin-bottom: 40px; page-break-inside: avoid; }
            .sub-header { background: #f8f9fa; padding: 15px; border-radius: 8px; margin-bottom: 15px; }
            .sub-name { font-size: 18px; font-weight: bold; color: #2c3e50; }
            .sub-type-badge { display: inline-block; padding: 4px 12px; border-radius: 12px; font-size: 12px; font-weight: bold; margin-left: 10px; }
            .external-badge { background: #e3f2fd; color: #1565c0; }
            .internal-badge { background: #f3e5f5; color: #6a1b9a; }
            .sub-total { font-size: 20px; font-weight: bold; color: #28a745; float: right; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            th, td { padding: 8px; text-align: left; border-bottom: 1px solid #ddd; font-size: 12px; }
            th { background-color: #f2f2f2; font-weight: bold; }
            .amount { text-align: right; }
            .footer { margin-top: 40px; font-size: 12px; color: #666; text-align: center; }
            .page-break { page-break-after: always; }
            .overview-table { margin-bottom: 40px; }
            .overview-table th, .overview-table td { padding: 12px; font-size: 14px; }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="company-name">${currentUser?.tenantName || 'Relief Roster'}</div>
            <div class="report-title">Annual Substitute Pay Report - ${selectedYear}</div>
            <p>Generated on ${format(new Date(), 'MMMM d, yyyy')}</p>
          </div>
          
          <div class="summary-box">
            <h2>Total Substitute Compensation for ${selectedYear}</h2>
            <div class="grand-total">$${grandTotal.toFixed(2)}</div>
            <p>Across ${allSubsData.length} substitute(s)</p>
          </div>
          
          <h2>Summary by Substitute</h2>
          <table class="overview-table">
            <thead>
              <tr>
                <th>Substitute Name</th>
                <th>Type</th>
                <th>Email</th>
                <th>Jobs Completed</th>
                <th class="amount">Total Paid</th>
              </tr>
            </thead>
            <tbody>
              ${allSubsData.map(({ sub, jobs, total }) => `
                <tr>
                  <td>${sub.name}</td>
                  <td>
                    <span class="sub-type-badge ${sub.subType === 'internal' ? 'internal-badge' : 'external-badge'}">
                      ${sub.subType === 'internal' ? 'Internal' : 'External'}
                    </span>
                  </td>
                  <td>${sub.email || 'N/A'}</td>
                  <td>${jobs.length}</td>
                  <td class="amount">$${total.toFixed(2)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          
          <div class="page-break"></div>
          
          <h2>Detailed Breakdown</h2>
          ${allSubsData.map(({ sub, jobs, grouped, total }) => `
            <div class="sub-section">
              <div class="sub-header">
                <span class="sub-name">${sub.name}</span>
                <span class="sub-type-badge ${sub.subType === 'internal' ? 'internal-badge' : 'external-badge'}">
                  ${sub.subType === 'internal' ? 'Internal' : 'External'}
                </span>
                <span class="sub-total">$${total.toFixed(2)}</span>
                <div style="clear: both;"></div>
                <small>Daily Rate: $${sub.dailyRate || 0} | Period Rate: $${sub.periodRate || sub.payRate || 0}</small>
              </div>
              
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Description</th>
                    <th>Department</th>
                    <th>Period</th>
                    <th class="amount">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  ${jobs.map(job => `
                    <tr>
                      <td>${job.jobDate ? format(job.jobDate, 'MM/dd/yyyy') : 'N/A'}</td>
                      <td>${job.description || 'N/A'}</td>
                      <td>${job.department || 'N/A'}</td>
                      <td>${job.period || 'N/A'}</td>
                      <td class="amount">$${parseFloat(job.payAmount || 0).toFixed(2)}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
          `).join('')}
          
          <div class="footer">
            <p>This report includes only jobs marked as "Paid" for accurate tax reporting.</p>
            <p>Total number of substitute teachers: ${allSubsData.length}</p>
            <p>Total compensation distributed: $${grandTotal.toFixed(2)}</p>
          </div>
        </body>
        </html>
      `;

      const printWindow = window.open('', '_blank');
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      printWindow.print();
      
    } catch (err) {
      console.error('Error generating report:', err);
      alert('Failed to generate report.');
    } finally {
      setLoading(false);
    }
  }, [substitutes, selectedYear, currentUser, getPayMethodForDate]);

  // Fetch all substitutes (Outside Sub role AND Substitute role)
  const fetchSubstitutes = useCallback(async () => {
  if (!currentUser?.tenantId || !isHR) {
    setError("You must be HR to access pay management.");
    setLoading(false);
    return;
  }

  try {
    setLoading(true);
    
    // Query for external subs (Outside Sub role)
    const externalSubsQuery = query(
      collection(db, 'faculty_staff'),
      where('tenantId', '==', currentUser.tenantId),
      where('role', '==', 'Outside Sub')
    );
    
    // Query for internal subs (Substitute role)
    const internalSubsQuery = query(
      collection(db, 'faculty_staff'),
      where('tenantId', '==', currentUser.tenantId),
      where('role', '==', 'Substitute')
    );
    
    // NEW: Query for anyone with pay tracking enabled
    const payTrackingQuery = query(
      collection(db, 'faculty_staff'),
      where('tenantId', '==', currentUser.tenantId),
      where('payTrackingEnabled', '==', true)
    );
    
    // Execute all three queries
    const [externalSnapshot, internalSnapshot, payTrackingSnapshot] = await Promise.all([
      getDocs(externalSubsQuery),
      getDocs(internalSubsQuery),
      getDocs(payTrackingQuery)
    ]);
    
    // Combine and mark sub type
    const externalSubs = externalSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      subType: 'external'
    }));
    
    const internalSubs = internalSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      subType: 'internal'
    }));
    
    const payTrackingSubs = payTrackingSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      subType: 'internal'
    }));
    
    // Merge and deduplicate (in case someone has both role=Substitute AND payTrackingEnabled)
    const allSubsMap = new Map();
    
    [...externalSubs, ...internalSubs, ...payTrackingSubs].forEach(sub => {
      if (!allSubsMap.has(sub.id)) {
        allSubsMap.set(sub.id, sub);
      }
    });
    
    const allSubs = Array.from(allSubsMap.values());
    
    console.log('Fetched substitutes:', allSubs);
    setSubstitutes(allSubs);
    setError('');
  } catch (err) {
    console.error('Error fetching substitutes:', err);
    setError('Failed to load substitutes.');
  } finally {
    setLoading(false);
  }
}, [currentUser, isHR]);

  // Fetch jobs for selected substitute with validation against sub_requests
  const fetchSubJobs = useCallback(async (substituteId) => {
    if (!substituteId || !currentUser?.tenantId) return;

    try {
      setLoading(true);
      
      const jobsQuery = query(
        collection(db, 'sub_jobs'),
        where('substituteId', '==', substituteId),
        where('tenantId', '==', currentUser.tenantId)
      );
      
      const jobsSnapshot = await getDocs(jobsQuery);
      
      const requestsQuery = query(
        collection(db, 'sub_requests'),
        where('tenantId', '==', currentUser.tenantId)
      );
      const requestsSnapshot = await getDocs(requestsQuery);
      
      const currentlyFilled = new Map();
      requestsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.filled && Object.keys(data.filled).length > 0) {
          Object.entries(data.filled).forEach(([period, filledInfo]) => {
            if (filledInfo && filledInfo.email) {
              const key = `${doc.id}-${period}`;
              currentlyFilled.set(key, {
                email: filledInfo.email,
                substituteId: filledInfo.uid || filledInfo.substituteId
              });
            }
          });
        }
      });
      
      const jobsList = [];
      const orphanedJobs = [];
      
      jobsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        
        let jobDate = null;
        if (data.jobDate?.toDate) {
          jobDate = data.jobDate.toDate();
        } else if (data.jobDate?.seconds) {
          jobDate = new Date(data.jobDate.seconds * 1000);
        } else if (typeof data.jobDate === 'string') {
          jobDate = parseISO(data.jobDate);
        } else if (data.jobDate instanceof Date) {
          jobDate = data.jobDate;
        }
        
        const job = {
          id: doc.id,
          ...data,
          jobDate: jobDate
        };
        
        const jobKey = `${data.subRequestId}-${data.period}`;
        const currentFill = currentlyFilled.get(jobKey);
        
        if (currentFill && currentFill.substituteId === substituteId) {
          jobsList.push(job);
        } else {
          orphanedJobs.push(job);
        }
      });
      
      if (orphanedJobs.length > 0) {
        const shouldCleanup = window.confirm(
          `Found ${orphanedJobs.length} orphaned job record(s) that no longer correspond to filled positions. Would you like to delete them?`
        );
        
        if (shouldCleanup) {
          await Promise.all(
            orphanedJobs.map(job => deleteDoc(doc(db, 'sub_jobs', job.id)))
          );
        }
      }
      
      setSubJobs(jobsList);
    } catch (err) {
      console.error('Error fetching sub jobs:', err);
      setError('Failed to load jobs.');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // Group jobs by date and calculate daily totals
  const groupJobsByDate = (jobs) => {
    const filtered = jobs.filter(job => {
      const jobYear = job.jobDate?.getFullYear();
      return jobYear === selectedYear;
    });

    const grouped = {};
    
    filtered.forEach(job => {
      const dateKey = job.jobDate ? format(job.jobDate, 'yyyy-MM-dd') : 'invalid-date';
      if (!grouped[dateKey]) {
        grouped[dateKey] = {
          date: job.jobDate,
          jobs: [],
          formattedDate: job.jobDate ? format(job.jobDate, 'MM/dd/yyyy') : 'Invalid Date'
        };
      }
      grouped[dateKey].jobs.push(job);
    });

    return Object.entries(grouped)
      .sort(([aKey], [bKey]) => {
        if (aKey === 'invalid-date' && bKey === 'invalid-date') return 0;
        if (aKey === 'invalid-date') return 1;
        if (bKey === 'invalid-date') return -1;
        return bKey.localeCompare(aKey);
      })
      .map(([dateKey, data]) => ({ dateKey, ...data }));
  };

  useEffect(() => {
    fetchSubstitutes();
  }, [fetchSubstitutes]);

  useEffect(() => {
    if (selectedSub) {
      fetchSubJobs(selectedSub.id);
    }
  }, [selectedSub, fetchSubJobs]);

  useEffect(() => {
    if (showAddInternalSubs) {
      fetchAllFacultyStaff();
    }
  }, [showAddInternalSubs, fetchAllFacultyStaff]);

  // Handle deleting a job
  const handleDeleteJob = async (jobId, description) => {
    if (!window.confirm(`Are you sure you want to delete the job: ${description}?`)) {
      return;
    }

    try {
      setSaving(true);
      await deleteDoc(doc(db, 'sub_jobs', jobId));
      
      if (selectedSub) {
        await fetchSubJobs(selectedSub.id);
      }
      
      alert('Job deleted successfully.');
    } catch (err) {
      console.error('Error deleting job:', err);
      alert('Failed to delete job.');
    } finally {
      setSaving(false);
    }
  };

  // Handle editing pay amount
  const handleEditPay = (jobId, currentAmount) => {
    setEditingJobId(jobId);
    setEditingPayAmount(currentAmount.toString());
  };

  const handleSavePayEdit = async (jobId) => {
    const newAmount = parseFloat(editingPayAmount);
    if (isNaN(newAmount) || newAmount < 0) {
      alert('Please enter a valid pay amount.');
      return;
    }

    try {
      setSaving(true);
      await updateDoc(doc(db, 'sub_jobs', jobId), {
        payAmount: newAmount
      });
      
      if (selectedSub) {
        await fetchSubJobs(selectedSub.id);
      }
      
      setEditingJobId(null);
      setEditingPayAmount('');
    } catch (err) {
      console.error('Error updating pay amount:', err);
      alert('Failed to update pay amount.');
    } finally {
      setSaving(false);
    }
  };

  // Handle updating substitute pay settings
  const handleUpdateSubPaySettings = async (field, value) => {
    if (!selectedSub) return;

    try {
      setSaving(true);
      await updateDoc(doc(db, 'faculty_staff', selectedSub.id), {
        [field]: value
      });
      
      setSelectedSub(prev => ({
        ...prev,
        [field]: value
      }));
      
      setSubstitutes(prev => 
        prev.map(sub => 
          sub.id === selectedSub.id ? { ...sub, [field]: value } : sub
        )
      );
    } catch (err) {
      console.error('Error updating pay settings:', err);
      alert('Failed to update pay settings.');
    } finally {
      setSaving(false);
    }
  };

  // Handle updating job status
  const handleUpdateJobStatus = async (jobId, newStatus) => {
    try {
      setSaving(true);
      await updateDoc(doc(db, 'sub_jobs', jobId), {
        status: newStatus
      });
      
      if (selectedSub) {
        await fetchSubJobs(selectedSub.id);
      }
    } catch (err) {
      console.error('Error updating job status:', err);
      alert('Failed to update job status.');
    } finally {
      setSaving(false);
    }
  };

  const groupedJobs = selectedSub ? groupJobsByDate(subJobs) : [];
  
  const totalPaidForYear = groupedJobs.reduce((sum, day) => {
    const paidJobs = day.jobs.filter(job => job.status === 'Paid');
    if (paidJobs.length === 0) return sum;
    
    const method = getPayMethodForDate(day.dateKey);
    const dailyRate = parseFloat(
      selectedSub.dailyRate || 
      selectedSub.payRate || 
      selectedSub.rate || 
      0
    );
    const periodRate = parseFloat(
      selectedSub.periodRate || 
      selectedSub.payRate || 
      selectedSub.rate || 
      0
    );
    
    if (method === 'daily') {
      return sum + dailyRate;
    } else {
      return sum + (paidJobs.length * periodRate);
    }
  }, 0);

  const filteredSubstitutes = substitutes.filter(sub => {
    if (filterSubType === 'all') return true;
    return sub.subType === filterSubType;
  });

  // Get faculty members who are already in the pay system
  const facultyInPaySystem = new Set(substitutes.map(sub => sub.id));
  
  // Filter to show only faculty not yet in pay system, excluding "Outside Sub" role
  const availableFaculty = allFacultyStaff.filter(faculty => 
    !facultyInPaySystem.has(faculty.id) && faculty.role !== 'Outside Sub'
  );

  if (!isHR) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>Access Denied</h2>
        <p>You must have HR privileges to access pay management.</p>
      </div>
    );
  }

  if (loading && substitutes.length === 0) {
    return <div style={{ padding: '20px' }}>Loading substitutes...</div>;
  }

  if (error && substitutes.length === 0) {
    return <div style={{ padding: '20px', color: 'red' }}>Error: {error}</div>;
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1>Sub Pay Management</h1>
      
      {/* Add Internal Subs Button */}
      <div style={{ marginBottom: '20px', textAlign: 'right' }}>
        <button
          onClick={() => setShowAddInternalSubs(!showAddInternalSubs)}
          style={{
            padding: '10px 20px',
            backgroundColor: '#6f42c1',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '1em',
            fontWeight: 'bold'
          }}
        >
          {showAddInternalSubs ? '✕ Close' : '+ Add Internal Subs to Pay System'}
        </button>
      </div>

      {/* Add Internal Subs Section */}
      {showAddInternalSubs && (
        <section style={{ 
          marginBottom: '30px', 
          padding: '20px', 
          border: '2px solid #6f42c1', 
          borderRadius: '8px',
          backgroundColor: '#f8f9fa'
        }}>
          <h2 style={{ color: '#6f42c1' }}>Add Internal Faculty/Staff to Pay System</h2>
          <p style={{ color: '#666', marginBottom: '20px' }}>
            Select internal employees who work as substitutes to enable pay tracking for them.
          </p>
          
          {loadingFaculty ? (
            <div>Loading faculty/staff...</div>
          ) : availableFaculty.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              All eligible faculty members are already in the pay system.
            </div>
          ) : (
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', 
              gap: '15px',
              maxHeight: '500px',
              overflowY: 'auto',
              padding: '10px'
            }}>
              {availableFaculty.map(faculty => (
                <div
                  key={faculty.id}
                  style={{
                    padding: '15px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <div>
                    <h4 style={{ margin: '0 0 5px 0' }}>{faculty.name}</h4>
                    <p style={{ margin: '0', fontSize: '0.85em', color: '#666' }}>
                      {faculty.role || 'No role assigned'}
                    </p>
                    <p style={{ margin: '5px 0 0 0', fontSize: '0.8em', color: '#999' }}>
                      {faculty.email || 'No email'}
                    </p>
                  </div>
                  <button
                    onClick={() => handleEnablePayTracking(faculty)}
                    disabled={saving}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.85em',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    + Add to Pay
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>
      )}
      
      {/* Substitute Selection */}
      <section style={{ marginBottom: '30px', padding: '20px', border: '1px solid #ddd', borderRadius: '8px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h2>Select Substitute</h2>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <label style={{ fontWeight: 'bold', marginRight: '5px' }}>Filter:</label>
            <select
              value={filterSubType}
              onChange={(e) => setFilterSubType(e.target.value)}
              style={{
                padding: '6px 12px',
                borderRadius: '4px',
                border: '1px solid #ccc',
                fontSize: '0.9em'
              }}
            >
              <option value="all">All Substitutes ({substitutes.length})</option>
              <option value="external">External Only ({substitutes.filter(s => s.subType === 'external').length})</option>
              <option value="internal">Internal Only ({substitutes.filter(s => s.subType === 'internal').length})</option>
            </select>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '15px' }}>
          {filteredSubstitutes.map(sub => (
            <div
              key={sub.id}
              onClick={() => setSelectedSub(sub)}
              style={{
                padding: '15px',
                border: selectedSub?.id === sub.id ? '2px solid #007bff' : '1px solid #ddd',
                borderRadius: '8px',
                cursor: 'pointer',
                backgroundColor: selectedSub?.id === sub.id ? '#f0f8ff' : 'white',
                position: 'relative'
              }}
            >
              <div style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                padding: '4px 12px',
                borderRadius: '12px',
                fontSize: '11px',
                fontWeight: 'bold',
                backgroundColor: sub.subType === 'internal' ? '#f3e5f5' : '#e3f2fd',
                color: sub.subType === 'internal' ? '#6a1b9a' : '#1565c0'
              }}>
                {sub.subType === 'internal' ? 'Internal' : 'External'}
              </div>
              <h3 style={{ margin: '0 0 10px 0', paddingRight: '80px' }}>{sub.name}</h3>
              <p style={{ margin: '5px 0', fontSize: '0.9em' }}>
                <strong>Daily Rate:</strong> ${sub.dailyRate || 0}/day
              </p>
              <p style={{ margin: '5px 0', fontSize: '0.9em' }}>
                <strong>Period Rate:</strong> ${sub.periodRate || sub.payRate || 0}/period
              </p>
              <p style={{ margin: '5px 0', fontSize: '0.9em' }}>
                <strong>Daily Max:</strong> ${sub.dailyMaxLimit || sub.dailyRate || 0}
              </p>
            </div>
          ))}
        </div>
        {filteredSubstitutes.length === 0 && (
          <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
            No substitutes found matching the selected filter.
          </div>
        )}
      </section>

      {selectedSub && (
        <>
          {/* Pay Settings Configuration */}
          <section style={{ marginBottom: '30px', padding: '20px', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f9f9f9' }}>
            <h2>Pay Settings for {selectedSub.name} 
              <span style={{
                marginLeft: '10px',
                padding: '4px 12px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 'bold',
                backgroundColor: selectedSub.subType === 'internal' ? '#f3e5f5' : '#e3f2fd',
                color: selectedSub.subType === 'internal' ? '#6a1b9a' : '#1565c0'
              }}>
                {selectedSub.subType === 'internal' ? 'Internal Employee' : 'External Contractor'}
              </span>
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
              
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Daily Rate ($):
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={selectedSub.dailyRate || ''}
                  onChange={(e) => handleUpdateSubPaySettings('dailyRate', parseFloat(e.target.value) || 0)}
                  disabled={saving}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                  placeholder="150.00"
                />
                <small style={{ color: '#666', fontSize: '0.8em' }}>
                  Standard daily pay rate
                </small>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Per Period Rate ($):
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={selectedSub.periodRate || selectedSub.payRate || ''}
                  onChange={(e) => handleUpdateSubPaySettings('periodRate', parseFloat(e.target.value) || 0)}
                  disabled={saving}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                  placeholder="40.00"
                />
                <small style={{ color: '#666', fontSize: '0.8em' }}>
                  Pay per individual class period
                </small>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Daily Maximum Limit ($):
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={selectedSub.dailyMaxLimit || selectedSub.dailyRate || ''}
                  onChange={(e) => handleUpdateSubPaySettings('dailyMaxLimit', parseFloat(e.target.value) || 0)}
                  disabled={saving}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                  placeholder="150.00"
                />
                <small style={{ color: '#666', fontSize: '0.8em' }}>
                  Maximum pay allowed per day (defaults to daily rate)
                </small>
              </div>
            </div>
          </section>

          {/* Year Selection and Summary */}
          <section style={{ marginBottom: '30px', padding: '20px', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#e8f4f8' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h2 style={{ margin: 0 }}>Job History for {selectedSub.name}</h2>
              <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                <button
                  onClick={() => fetchSubJobs(selectedSub.id)}
                  disabled={saving}
                  style={{
                    padding: '5px 10px',
                    backgroundColor: '#17a2b8',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.9em'
                  }}
                >
                  🔄 Sync Jobs
                </button>
                <div>
                  <label style={{ marginRight: '10px', fontWeight: 'bold' }}>Year:</label>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                    style={{ padding: '5px 10px', borderRadius: '4px', border: '1px solid #ccc' }}
                  >
                    {[2024, 2025, 2026].map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            
            <div style={{ padding: '15px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #ccc' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <div>
                  <h3 style={{ margin: '0 0 10px 0' }}>Annual Pay Summary</h3>
                  <p style={{ fontSize: '1.2em', fontWeight: 'bold', margin: 0 }}>
                    Total paid for {selectedYear}: ${totalPaidForYear.toFixed(2)}
                  </p>
                  <p style={{ fontSize: '0.9em', color: '#666', margin: '5px 0 0 0' }}>
                    Only includes jobs marked as "Paid"
                  </p>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <button
                    onClick={generateIndividualPDF}
                    disabled={saving || !selectedSub}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.9em'
                    }}
                  >
                    📄 Generate Pay Report
                  </button>
                  <button
                    onClick={generateAllSubsPDF}
                    disabled={saving}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#6f42c1',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.9em'
                    }}
                  >
                    📊 All Subs Report
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* Jobs grouped by date */}
          {loading ? (
            <div>Loading jobs...</div>
          ) : groupedJobs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              No jobs found for {selectedYear}.
              <br />
              <small>Debug: Found {subJobs.length} total jobs for this substitute</small>
            </div>
          ) : (
            <div key={`${selectedSub?.id}-${selectedYear}`}>
              {groupedJobs.map(({ dateKey, date, jobs, formattedDate }, groupIndex) => {
                const currentPayMethod = getPayMethodForDate(dateKey);
                const calculatedDailyPay = calculateDailyPay(jobs, dateKey);
                const dailyMaxLimit = parseFloat(selectedSub.dailyMaxLimit || selectedSub.dailyRate || 0);
                const isOverDailyLimit = currentPayMethod === 'period' && calculatedDailyPay > dailyMaxLimit;
                
                const allJobsPaid = jobs.every(job => job.status === 'Paid');
                const allJobsUnpaid = jobs.every(job => job.status === 'Unpaid');
                
                return (
                  <section
                    key={`${dateKey}-${groupIndex}`}
                    style={{
                      marginBottom: '25px',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      overflow: 'hidden'
                    }}
                  >
                    <div
                      style={{
                        padding: '15px 20px',
                        backgroundColor: isOverDailyLimit ? '#fff3cd' : '#f8f9fa',
                        borderBottom: '1px solid #ddd'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <h3 style={{ margin: 0 }}>{formattedDate}</h3>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: '1.1em', fontWeight: 'bold' }}>
                            Daily Pay: ${calculatedDailyPay.toFixed(2)}
                          </div>
                          <div style={{ fontSize: '0.9em', color: '#666' }}>
                            {currentPayMethod === 'daily' ? 
                              `Daily Rate: ${selectedSub.dailyRate || 0} (${jobs.length} periods)` : 
                              `Per Period: ${jobs.length} × ${selectedSub.periodRate || selectedSub.payRate || 0} = ${(jobs.length * parseFloat(selectedSub.periodRate || selectedSub.payRate || 0)).toFixed(2)}`
                            }
                            {isOverDailyLimit && (
                              <span style={{ color: '#856404', marginLeft: '10px' }}>
                                ⚠️ Exceeds daily limit (${dailyMaxLimit})
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '15px', padding: '10px', backgroundColor: 'rgba(255,255,255,0.7)', borderRadius: '4px' }}>
                        <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                          <span style={{ fontWeight: 'bold', fontSize: '0.9em' }}>Pay Method:</span>
                          <select
                            value={currentPayMethod}
                            onChange={(e) => setPayMethodForDate(dateKey, e.target.value)}
                            style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              border: '1px solid #ccc',
                              fontSize: '0.9em',
                              backgroundColor: 'white'
                            }}
                          >
                            <option value="daily">Daily Rate (${selectedSub.dailyRate || 0})</option>
                            <option value="period">Per Period (${selectedSub.periodRate || selectedSub.payRate || 0} × {jobs.length} = ${(jobs.length * parseFloat(selectedSub.periodRate || selectedSub.payRate || 0)).toFixed(2)})</option>
                          </select>
                        </div>

                        <div style={{ display: 'flex', gap: '10px' }}>
                          {!allJobsPaid && (
                            <button
                              onClick={() => handleMarkDayAsPaid(jobs, dateKey)}
                              disabled={saving}
                              style={{
                                padding: '6px 12px',
                                backgroundColor: '#28a745',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.85em',
                                fontWeight: 'bold'
                              }}
                            >
                              💰 Mark Day as Paid
                            </button>
                          )}
                          
                          {!allJobsUnpaid && (
                            <button
                              onClick={() => handleMarkDayAsUnpaid(jobs)}
                              disabled={saving}
                              style={{
                                padding: '6px 12px',
                                backgroundColor: '#dc3545',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.85em',
                                fontWeight: 'bold'
                              }}
                            >
                              ❌ Mark Day as Unpaid
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    <div style={{ padding: '0' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#f2f2f2' }}>
                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                              Description
                            </th>
                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                              Individual Pay
                            </th>
                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                              Status
                            </th>
                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {jobs.map((job, index) => (
                            <tr key={job.id} style={{ borderBottom: index < jobs.length - 1 ? '1px solid #eee' : 'none' }}>
                              <td style={{ padding: '12px' }}>
                                {job.description || `${job.department || 'Unknown'} - Period ${job.period || 'N/A'}`}
                              </td>
                              <td style={{ padding: '12px' }}>
                                {editingJobId === job.id ? (
                                  <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                                    <span>$</span>
                                    <input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      value={editingPayAmount}
                                      onChange={(e) => setEditingPayAmount(e.target.value)}
                                      style={{ width: '80px', padding: '4px' }}
                                      onKeyPress={(e) => {
                                        if (e.key === 'Enter') handleSavePayEdit(job.id);
                                        if (e.key === 'Escape') {
                                          setEditingJobId(null);
                                          setEditingPayAmount('');
                                        }
                                      }}
                                    />
                                    <button
                                      onClick={() => handleSavePayEdit(job.id)}
                                      disabled={saving}
                                      style={{ padding: '2px 6px', fontSize: '0.8em', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '3px' }}
                                    >
                                      ✓
                                    </button>
                                    <button
                                      onClick={() => {
                                        setEditingJobId(null);
                                        setEditingPayAmount('');
                                      }}
                                      disabled={saving}
                                      style={{ padding: '2px 6px', fontSize: '0.8em', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '3px' }}
                                    >
                                      ✗
                                    </button>
                                  </div>
                                ) : (
                                  <span
                                    onClick={() => handleEditPay(job.id, job.payAmount)}
                                    style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                    title="Click to edit"
                                  >
                                    ${parseFloat(job.payAmount || 0).toFixed(2)}
                                  </span>
                                )}
                              </td>
                              <td style={{ padding: '12px' }}>
                                <select
                                  value={job.status || 'Unpaid'}
                                  onChange={(e) => handleUpdateJobStatus(job.id, e.target.value)}
                                  disabled={saving}
                                  style={{
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    border: '1px solid #ccc',
                                    fontSize: '0.9em',
                                    backgroundColor: job.status === 'Paid' ? '#d4edda' : '#fff3cd',
                                    color: job.status === 'Paid' ? '#155724' : '#856404'
                                  }}
                                >
                                  <option value="Unpaid">Unpaid</option>
                                  <option value="Paid">Paid</option>
                                </select>
                              </td>
                              <td style={{ padding: '12px' }}>
                                <button
                                  onClick={() => handleDeleteJob(job.id, job.description)}
                                  disabled={saving}
                                  style={{
                                    padding: '4px 8px',
                                    fontSize: '0.8em',
                                    backgroundColor: '#dc3545',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                  }}
                                >
                                  Delete
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SubPayManagement;